import { applyPageMetadata } from "@/lib/page-metadata";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout, Section, Eyebrow, CTAButton } from "@/components/site/Layout";
import { TalkToStrategist } from "@/components/site/TalkToStrategist";
import { practiceAreas } from "@/data/practice-areas";
import { services } from "@/data/services";
import { blogPosts } from "@/data/blog-posts";
import { SeoPlanFinder } from "@/components/site/SeoPlanFinder";
import { CustomReportForm } from "@/components/site/CustomReportForm";
import { HeroLeadForm } from "@/components/site/HeroLeadForm";
import { HeroWebsiteCheckBar } from "@/components/site/HeroWebsiteCheckBar";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { caseStudies } from "@/data/case-studies";
import heroImage from "@/assets/home-hero.webp";
import { BLOG_VISUALS } from "@/data/blog-listing";
import { glossaryTerms, getTermBySlug } from "@/data/glossary";
import { Search, MapPin, Sparkles, Monitor, PenLine, Link2, Megaphone, Wrench, Target, Share2, BarChart3, Scale, Building2, Crown, Layers } from "lucide-react";
import { practiceIcons } from "@/components/icons/practiceIcons";
import courthouseImage from "@/assets/practice-courthouse.jpg";
import { TestimonialCarousel } from "@/components/site/TestimonialCarousel";

import svcLawFirmSeo from "@/assets/services/law-firm-seo.jpg";
import svcLocal from "@/assets/services/local-seo.jpg";
import svcAi from "@/assets/services/ai-seo-geo.jpg";
import svcWeb from "@/assets/services/website-development.jpg";
import svcContent from "@/assets/services/legal-content-writing.jpg";
import svcLinks from "@/assets/services/link-building-v3.jpg";
import svcPpc from "@/assets/services/ppc.jpg";
import svcTech from "@/assets/services/technical-seo-v2.jpg";
import svcLeads from "@/assets/services/lead-generation.jpg";
import svcSocial from "@/assets/services/social-media.jpg";
import svcAnalytics from "@/assets/services/analytics.jpg";

const serviceImages: Record<string, { src: string; alt: string }> = {
  "law-firm-seo": { src: svcLawFirmSeo, alt: "Judge's gavel on a wooden desk" },
  "local-seo-for-law-firms": { src: svcLocal, alt: "City map with location pins" },
  "ai-seo-geo": { src: svcAi, alt: "Hands typing on a laptop with an AI interface" },
  "law-firm-website-development": { src: svcWeb, alt: "Laptop and phone on a desk workspace" },
  "legal-content-writing": { src: svcContent, alt: "Fountain pen resting on legal documents" },
  "link-building-for-law-firms": { src: svcLinks, alt: "Legal publication pages linked to a law firm website" },
  "ppc-for-law-firms": { src: svcPpc, alt: "Monitor showing performance charts" },
  "technical-seo-for-law-firms": { src: svcTech, alt: "Laptop showing a technical SEO audit dashboard" },
  "lead-generation-for-law-firms": { src: svcLeads, alt: "Two professionals shaking hands in an office" },
  "social-media-marketing-for-law-firms": { src: svcSocial, alt: "Hand holding a smartphone with a social feed" },
  "law-firm-marketing-analytics": { src: svcAnalytics, alt: "Rising bar chart showing growth" },
};

const serviceIcons: Record<string, typeof Search> = {
  "law-firm-seo": Search,
  "local-seo-for-law-firms": MapPin,
  "ai-seo-geo": Sparkles,
  "law-firm-website-development": Monitor,
  "legal-content-writing": PenLine,
  "link-building-for-law-firms": Link2,
  "ppc-for-law-firms": Megaphone,
  "technical-seo-for-law-firms": Wrench,
  "lead-generation-for-law-firms": Target,
  "social-media-marketing-for-law-firms": Share2,
  "law-firm-marketing-analytics": BarChart3,
};

const homeFaqs = [
  { q: "How long does law firm SEO take to drive cases?", a: "Most firms see ranking movement in 60–90 days, qualified consults in 4–6 months, and a steady case pipeline by month 9–12. Personal injury and DUI markets are more competitive and skew toward the longer end; estate planning, family law, and immigration tend to compound faster." },
  { q: "Do you work with firms outside the United States?", a: "Yes. We currently work with firms in the US, Canada, UK, and Australia. Our local SEO playbook adapts to Google Business Profile, jurisdiction-specific schema, and the bar advertising rules in each market." },
  { q: "How do you report on ranking progress?", a: "No reputable SEO company will. We guarantee process, deliverables, transparency, and monthly reporting tied to consultations and signed cases, the things that actually move firm revenue. Anyone guaranteeing position #1 is selling you risk." },
  { q: "What does law firm SEO cost?", a: "Engagements typically run $3,500–$12,000/month depending on practice areas, geography, and competitive intensity. We publish indicative pricing on our pricing page and give you a fixed 12-month roadmap before you sign anything." },
  { q: "Do you use AI-generated content?", a: "We use AI to accelerate research, outlines, and editing, never as the final voice on the page. Every published page is written or rewritten by a human editor and reviewed for accuracy, bar compliance, and E-E-A-T signals (real attorney author, jurisdictions, citations)." },
  { q: "Can you fix a site that was hit by a Google update?", a: "Usually, yes. Our recovery playbook covers helpful-content audits, thin-content pruning, link profile cleanup, and entity rebuilding. We've recovered firms hit by HCU, March 2024 core, and the spam updates, typical recovery is 4–8 months." },
  { q: "Do I have to sign a long-term contract?", a: "Initial engagement is a 12-month roadmap because SEO needs that runway to compound. After month 12 it converts to month-to-month, we have to earn the renewal every month." },
  { q: "Who actually does the work?", a: "Your account is run by a senior legal-SEO strategist and an editor who reads case files and intake notes weekly. We don't offshore strategy or writing. You'll know your team by name and meet them on the kickoff call." },
];

export const Route = createFileRoute("/")({
  head: () => applyPageMetadata("/", {
    meta: [
      { title: "SEO Company for Lawyers & Law Firms | PageOne Lawyers" },
      { name: "description", content: "Specialist SEO agency for law firms. Personal injury, DUI, family, criminal defense, estate planning & more. Ethical SEO. Real cases. Page-one results." },
      { property: "og:title", content: "SEO Company for Lawyers & Law Firms | PageOne Lawyers" },
      { property: "og:description", content: "The SEO company built exclusively for attorneys. We rank law firms across every practice area." },
      { property: "og:url", content: "https://pageonelawyers.com/" },
    ],
    links: [{ rel: "canonical", href: "https://pageonelawyers.com/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "PageOne Lawyers",
          description: "SEO company for law firms across every practice area.",
          areaServed: "United States, United Kingdom, Canada, Australia",
          serviceType: "Search Engine Optimization for Law Firms",
          aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "127" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "How long does law firm SEO take to drive cases?", acceptedAnswer: { "@type": "Answer", text: "Most firms see ranking movement in 60–90 days, qualified consults in 4–6 months, and a steady case pipeline by month 9–12." } },
            { "@type": "Question", name: "What does law firm SEO cost?", acceptedAnswer: { "@type": "Answer", text: "Engagements typically run $3,500–$12,000/month depending on practice areas, geography, and competitive intensity." } },
            { "@type": "Question", name: "How do you report on ranking progress?", acceptedAnswer: { "@type": "Answer", text: "No reputable SEO company will. We guarantee process, deliverables, and monthly reporting tied to consultations and signed cases." } },
          ],
        }),
      },
    ],
  }),
  component: Home,
});

function Home() {
  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden bg-hero-gradient text-primary-foreground">
        <div className="absolute inset-0">
          <img src={heroImage} alt="" aria-hidden width={1280} height={880} fetchPriority="high" decoding="async" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--navy-deep)] via-[var(--navy-deep)]/92 to-[var(--navy-deep)]/70" />
        </div>
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="relative mx-auto max-w-7xl px-5 pb-16 pt-14 sm:px-6 sm:pb-28 sm:pt-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <Eyebrow><span className="text-[var(--gold)]">SEO for Law Firms</span></Eyebrow>
              <h1 className="mt-5 max-w-3xl font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight font-semibold">
                The SEO company built <span className="text-[var(--gold)]">exclusively</span> for attorneys.
              </h1>
              <p className="mt-6 text-lg md:text-xl text-primary-foreground/80 max-w-2xl leading-relaxed">
                We rank personal injury, DUI, family, criminal defense, and every other kind of law firm. No PBNs. No spam. No quarterly excuses. Just measurable case growth from organic search.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <CTAButton to="/law-firm-seo-audit" variant="gold">Get my free SEO audit</CTAButton>
                <CTAButton to="/case-studies" variant="ghostLight">See real case results</CTAButton>
              </div>
              <HeroWebsiteCheckBar />
              <div className="mt-10 grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-4 max-w-3xl">
                {[
                  ["+312%", "Avg. organic case growth"],
                  ["4.9★", "Client satisfaction"],
                  ["12 mo.", "Avg. payback period"],
                ].map(([n, l]) => (
                  <div key={l}>
                    <div className="font-display text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-[var(--gold)]">{n}</div>
                    <div className="mt-1 text-xs md:text-sm text-primary-foreground/70">{l}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5" id="hero-form">
              <HeroLeadForm />
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF — animated marquee */}
      <div className="overflow-hidden border-y border-border bg-secondary/40 py-5">
        <div className="trust-marquee gap-10 px-5 text-sm font-bold uppercase tracking-widest text-foreground sm:gap-14 sm:text-base">
          {[
            "Trusted by law firms across",
            "Personal Injury",
            "DUI / DWI",
            "Family Law",
            "Criminal Defense",
            "Estate Planning",
            "Immigration",
            "Trusted by law firms across",
            "Personal Injury",
            "DUI / DWI",
            "Family Law",
            "Criminal Defense",
            "Estate Planning",
            "Immigration",
          ].map((t) => (
            <span key={t + Math.random()} className="flex items-center gap-10 whitespace-nowrap sm:gap-14">
              <span>{t}</span>
              <span className="text-[var(--gold)]" aria-hidden="true">◆</span>
            </span>
          ))}
        </div>
      </div>

      {/* PRACTICE AREAS */}
      <Section>
        <div className="grid md:grid-cols-2 gap-12 mb-14">
          <div>
            <Eyebrow>Practice areas we rank</Eyebrow>
            <h2 className="mt-4 text-4xl md:text-5xl font-semibold">Specialist SEO for your specific practice.</h2>
          </div>
          <p className="text-lg text-muted-foreground self-end">
            Generalist agencies recycle the same playbook. We've built dedicated keyword maps, content frameworks, and link strategies for every major area of legal practice.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,3fr)] lg:gap-6">
          <div className="relative mx-auto w-full max-w-sm self-start lg:sticky lg:top-28 lg:max-w-none">
            <img
              src={courthouseImage}
              alt="Classical courthouse with tall marble columns"
              width={768}
              height={1152}
              loading="lazy"
              decoding="async"
              className="relative z-10 h-[360px] w-full object-cover object-left-top sm:h-[440px] lg:h-[600px]"
              style={{
                WebkitMaskImage:
                  "linear-gradient(to bottom, transparent 0%, #000 10%, #000 55%, transparent 98%), linear-gradient(to right, transparent 0%, #000 10%, #000 78%, transparent 100%), radial-gradient(ellipse 95% 90% at 45% 35%, #000 60%, transparent 100%)",
                WebkitMaskComposite: "source-in, source-in",
                maskImage:
                  "linear-gradient(to bottom, transparent 0%, #000 10%, #000 55%, transparent 98%), linear-gradient(to right, transparent 0%, #000 10%, #000 78%, transparent 100%), radial-gradient(ellipse 95% 90% at 45% 35%, #000 60%, transparent 100%)",
                maskComposite: "intersect",
              }}
            />
            <div className="absolute bottom-6 left-4 right-4 z-20 rounded-lg border border-border bg-card p-5 shadow-[var(--shadow-card)] lg:-right-4 lg:bottom-12">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-[#fbf3e3]">
                  <Scale className="h-5 w-5 text-[var(--gold-deep)]" strokeWidth={1.8} aria-hidden="true" />
                </span>
                <p className="font-display text-lg font-semibold leading-snug text-foreground">Focused on Law Firms</p>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">Strategy, content, and authority building tailored to your practice area.</p>
            </div>
          </div>
          <div className="relative z-10 grid grid-cols-1 gap-4 min-[480px]:grid-cols-2 xl:grid-cols-4">
            {practiceAreas.map((p) => {
              const Icon = practiceIcons[p.slug] ?? Building2;
              return (
                <Link
                  key={p.slug}
                  to="/practice-areas/$slug"
                  params={{ slug: p.slug }}
                  className="group flex h-full min-w-0 flex-col justify-between rounded-lg border border-border bg-card p-5 shadow-[0_4px_20px_-12px_rgba(10,22,40,0.12)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[var(--shadow-card)]"
                >
                  <div>
                    <span className="grid h-10 w-10 place-items-center rounded-[10px] bg-[#fbf3e3] transition-colors duration-300 group-hover:bg-[#f0dfbd]">
                      <Icon className="h-5 w-5 text-[var(--gold-deep)]" strokeWidth={1.8} aria-hidden="true" />
                    </span>
                    <h3 className="mt-4 text-lg font-semibold">{p.shortName}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{p.headline}</p>
                  </div>
                  <div className="mt-4 flex min-h-6 items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
                    Explore strategy <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </Section>

      {/* SERVICES */}
      <div className="bg-primary text-primary-foreground">
        <Section>
          <div className="mb-14">
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold)]">
              <span className="h-px w-8 bg-[var(--gold)]" />What we do
            </p>
            <h2 className="mt-4 max-w-3xl text-4xl md:text-5xl font-semibold text-primary-foreground">Every service a modern law firm needs to dominate search.</h2>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
            {services.map((s, i) => {
              const Icon = serviceIcons[s.slug] ?? Search;
              const img = serviceImages[s.slug];
              const wide = i >= services.length - 2;
              return (
                <Link
                  key={s.slug}
                  to="/services/$slug"
                  params={{ slug: s.slug }}
                  className={`group relative isolate flex min-h-[148px] overflow-hidden rounded-xl border border-white/10 bg-primary p-4 transition-all duration-300 hover:-translate-y-1 hover:border-white/25 hover:shadow-[0_18px_40px_-20px_rgba(0,0,0,0.7)] ${wide ? "lg:col-span-3 lg:p-5" : "lg:col-span-2"}`}
                >
                  {img && (
                    <img
                      src={img.src}
                      alt={img.alt}
                      loading="lazy"
                      decoding="async"
                      width={wide ? 1536 : 1088}
                      height={wide ? 640 : 608}
                      className="absolute inset-0 -z-20 h-full w-full object-cover object-right transition-transform duration-300 group-hover:scale-[1.03]"
                    />
                  )}
                  <div aria-hidden="true" className="absolute inset-0 -z-10 bg-[linear-gradient(90deg,var(--primary)_0%,color-mix(in_oklab,var(--primary)_88%,transparent)_40%,color-mix(in_oklab,var(--primary)_35%,transparent)_75%,color-mix(in_oklab,var(--primary)_15%,transparent)_100%)]" />
                  <div className={`flex min-w-0 flex-1 flex-col ${wide ? "lg:flex-row lg:items-stretch lg:gap-5" : ""}`}>
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-white/10 bg-primary/70">
                      <Icon className="h-4 w-4 text-[var(--gold)]" strokeWidth={1.5} aria-hidden="true" />
                    </span>
                    <div className="mt-2.5 flex max-w-[80%] min-w-0 flex-1 flex-col lg:mt-0 lg:max-w-[76%]">
                      <h3 className={`${wide ? "" : "lg:mt-2"} text-base font-semibold text-primary-foreground`}>{s.name}</h3>
                      <p className="mt-1 text-[13px] leading-snug text-primary-foreground/75">{s.tagline}</p>
                      <div className="mt-auto inline-flex items-center gap-1.5 pt-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--gold)]">
                        Learn more <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </Section>
      </div>

      {/* HOW WE WORK */}
      <Section>
        <Eyebrow>The PageOne process</Eyebrow>
        <h2 className="mt-4 text-4xl md:text-5xl font-semibold max-w-3xl">Four phases. Twelve months. Compounding results.</h2>
        <ol className="relative mt-14 grid gap-10 sm:grid-cols-2 sm:gap-x-8 lg:grid-cols-4 lg:gap-0">
          <span aria-hidden="true" className="pointer-events-none absolute left-[12.5%] right-[12.5%] top-8 hidden h-px bg-border lg:block" />
          {[1, 2, 3].map((i) => (
            <span key={i} aria-hidden="true" className="pointer-events-none absolute top-8 hidden h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--gold-deep)] lg:block" style={{ left: `${i * 25}%` }} />
          ))}
          {([
            ["01", "Diagnose", "Full technical, content, link, and competitor audit tied to your real case-type revenue.", Search],
            ["02", "Strategy", "A custom 12-month roadmap mapped to your practice areas, geography, and target case value.", Crown],
            ["03", "Execute", "Content shipped, tech fixed, links earned, local pack optimized, month over month.", Layers],
            ["04", "Compound", "Rankings stack. Reviews accumulate. Authority compounds. Cost per case keeps dropping.", BarChart3],
          ] as const).map(([n, t, d, Icon], idx) => (
            <li key={n} className={`relative grid grid-cols-[4rem_minmax(0,1fr)] gap-x-5 lg:block lg:px-6`}>
              {idx < 3 && <span aria-hidden="true" className="absolute left-8 top-16 -bottom-10 w-px bg-border sm:hidden" />}
              <div className="relative z-10 grid h-16 w-16 place-items-center rounded-full border border-border bg-card shadow-[0_4px_14px_-8px_color-mix(in_oklab,var(--foreground)_25%,transparent)] lg:mx-auto">
                <Icon className="h-6 w-6 text-[var(--gold-deep)]" strokeWidth={1.6} aria-hidden="true" />
              </div>
              <div className={`min-w-0 lg:mt-8 ${idx > 0 ? "lg:border-l lg:border-border/70 lg:-ml-6 lg:pl-6" : ""}`}>
                <h3 className="flex items-baseline gap-2 text-2xl font-semibold">
                  <span className="font-display text-[var(--gold-deep)]">{n}</span>
                  <span>{t}</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{d}</p>
              </div>
            </li>
          ))}
        </ol>
      </Section>

      {/* WHY US, EEAT */}
      <div className="bg-[var(--navy-deep)] text-primary-foreground">
        <Section className="!py-24">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <Eyebrow><span className="text-[var(--gold)]">Why PageOne</span></Eyebrow>
              <h2 className="mt-4 text-4xl md:text-5xl font-semibold">We've earned the right to talk about legal SEO.</h2>
              <p className="mt-6 text-primary-foreground/80 text-lg leading-relaxed">
                We've spent over a decade ranking law firms, and only law firms. Every team member has shipped legal SEO work. Every recommendation we make is grounded in real client outcomes, not hypothetical playbooks.
              </p>
            </div>
            <ul className="space-y-5">
              {[
                ["Legal-only since day one", "We don't take dentists, plumbers, or SaaS clients. Law firms are the entire practice."],
                ["Bar-compliance literacy", "Every piece of content reviewed against ABA Model Rule 7.x and state advertising rules."],
                ["Real attribution, not vanity", "Reports built around consultations and signed cases, not 'impressions' or 'keyword count'."],
                ["No long-term lock-in tricks", "Month-to-month after the initial 12-month roadmap completes. Earn the renewal."],
                ["Transparent deliverables", "You see every page shipped, every link earned, every technical fix made."],
              ].map(([t, d]) => (
                <li key={t} className="flex gap-4">
                  <span className="mt-1 flex-shrink-0 h-6 w-6 rounded-full bg-[var(--gold)] text-[var(--navy-deep)] flex items-center justify-center text-sm font-bold">✓</span>
                  <div>
                    <div className="font-semibold text-lg">{t}</div>
                    <div className="text-primary-foreground/70 text-sm mt-1">{d}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Section>
      </div>

      {/* TESTIMONIALS */}
      <Section>
        <Eyebrow>What our clients say</Eyebrow>
        <h2 className="mt-4 text-4xl md:text-5xl font-semibold max-w-3xl">Real partners. Real numbers. Real cases.</h2>
        <div className="mt-12">
          <TestimonialCarousel />
        </div>
      </Section>

      {/* CASE STUDIES */}
      <div className="bg-secondary/40 border-y border-border">
        <Section>
          <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_auto] md:items-start mb-10">
            <div className="min-w-0">
              <Eyebrow>Case studies</Eyebrow>
              <h2 className="mt-4 text-4xl md:text-5xl font-semibold text-foreground max-w-2xl">Proof, not promises.</h2>
              <p className="mt-4 text-lg text-muted-foreground max-w-2xl">Analytics screenshots, SERP proof, and GBP insights from real client engagements. No cherry-picked keywords. No stock testimonials.</p>
            </div>
            <Link to="/case-studies" className="justify-self-start md:mt-10 inline-flex items-center rounded-md border border-primary px-5 py-2.5 text-sm font-semibold text-primary transition-colors duration-200 hover:bg-primary hover:text-primary-foreground">View all case studies →</Link>
          </div>
          <ul className="border-t border-border">
            {caseStudies.map((c) => {
              const home: Record<string, { title: string; m: [string, string][] }> = {
                "personal-injury-houston": { title: "From page 4 to #1 for car accident lawyer in 11 months.", m: [["+371%", "Organic Traffic"], ["#1", "Rank"]] },
                "dui-defense-phoenix": { title: "After-hours mobile calls grew 417% with a DUI-specific strategy.", m: [["+417%", "Mobile Calls"], ["Local", "SEO"]] },
                "family-law-chicago": { title: "248% growth in divorce consultation across three Illinois counties.", m: [["+248%", "Consultations"], ["3", "Counties"]] },
              };
              const h = home[c.slug] ?? { title: "Wills, trusts, and probate inquiries up 184% in 9 months.", m: [["+184%", "Inquiries"], ["3.1x", "Leads"]] as [string, string][] };
              return (
                <li key={c.slug} className="border-b border-border">
                  <Link
                    to="/case-studies/$slug"
                    params={{ slug: c.slug }}
                    className="group grid grid-cols-[96px_minmax(0,1fr)_auto] sm:grid-cols-[140px_minmax(0,1fr)_auto] lg:grid-cols-[180px_minmax(0,1fr)_260px_auto] items-center gap-x-4 sm:gap-x-6 gap-y-3 py-5"
                  >
                    <div className="row-span-2 lg:row-span-1 aspect-[16/10] overflow-hidden rounded-md border border-border bg-card">
                      <img src={c.cover} alt={`${c.practice} SEO case study, ${c.market}`} width={1400} height={900} loading="lazy" className="h-full w-full object-cover object-left-top transition-transform duration-300 group-hover:scale-[1.04]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold uppercase tracking-wider"><span className="text-[var(--gold-deep)]">{c.practice}</span><span className="text-muted-foreground"> • {c.market}</span></div>
                      <h3 className="mt-1.5 text-base sm:text-xl font-semibold leading-snug text-foreground transition-colors duration-200 group-hover:text-[var(--gold-deep)]">{h.title}</h3>
                    </div>
                    <div className="col-start-2 row-start-2 lg:col-start-3 lg:row-start-1 grid grid-cols-2 gap-4 lg:border-l lg:border-border lg:pl-6">
                      {h.m.map(([v, l]) => (
                        <div key={l}>
                          <div className="text-xl sm:text-2xl font-semibold text-[var(--gold-deep)] leading-none">{v}</div>
                          <div className="mt-1 text-xs text-muted-foreground">{l}</div>
                        </div>
                      ))}
                    </div>
                    <span aria-hidden className="col-start-3 row-start-1 row-span-2 lg:col-start-4 lg:row-span-1 grid h-10 w-10 place-items-center rounded-full border border-border text-[var(--gold-deep)] transition-colors duration-200 group-hover:bg-primary/10 group-hover:border-primary">
                      <span className="transition-transform duration-200 group-hover:translate-x-0.5">→</span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </Section>
      </div>

      {/* SEO PLAN FINDER */}
      <SeoPlanFinder />

      {/* CUSTOM REPORT */}
      <Section>
        <CustomReportForm />
      </Section>

      {/* BLOG GRID */}
      <div className="bg-secondary/40 border-y border-border">
        <Section>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <Eyebrow>From the blog</Eyebrow>
              <h2 className="mt-4 text-4xl md:text-5xl font-semibold text-foreground max-w-2xl">Field-tested guides for law firm growth.</h2>
            </div>
            <Link to="/blog" className="border border-border px-6 py-3 text-xs font-bold uppercase tracking-[0.18em] text-foreground transition-all hover:bg-primary hover:text-primary-foreground">View all guides →</Link>
          </div>
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {blogPosts.slice(0, 6).map((p) => {
              const v = BLOG_VISUALS[p.slug];
              return (
                <Link key={p.slug} to="/blog/$slug" params={{ slug: p.slug }} className="group flex flex-col">
                  <div className="relative mb-6 aspect-[16/10] overflow-hidden rounded-md bg-secondary">
                    {v ? (
                      <img src={v.image} alt={v.alt} loading="lazy" className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]" />
                    ) : (
                      <div className="h-full w-full bg-primary/5" />
                    )}
                    {v ? (
                      <span className="absolute bottom-3 left-3 bg-background px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--gold-deep)] shadow-sm">{v.category}</span>
                    ) : null}
                  </div>
                  <h3 className="text-xl font-semibold text-foreground transition-colors group-hover:text-[var(--gold-deep)]">{p.shortTitle}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground line-clamp-3">{p.excerpt}</p>
                  <div className="mt-4 text-sm font-semibold text-foreground underline decoration-[var(--gold)]/40 decoration-2 underline-offset-4 group-hover:decoration-[var(--gold)]">Read guide</div>
                </Link>
              );
            })}
          </div>
        </Section>
       </div>

       {/* GLOSSARY CTA */}
       <Section className="!py-12 sm:!py-14">
         <div className="mx-auto max-w-4xl rounded-2xl border border-border bg-muted/40 px-6 py-10 text-center sm:px-10">
           <Eyebrow>Glossary</Eyebrow>
           <h2 className="mt-4 text-3xl sm:text-4xl font-semibold">Speak the language of law firm SEO.</h2>
           <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
             Plain-English definitions of the terms you'll see in audits, reports and strategy calls, from the Local Pack to GEO.
           </p>
           <div className="mt-7 flex flex-wrap justify-center gap-2">
             {["local-pack", "geo", "e-e-a-t", "local-services-ads", "cost-per-case", "aba-model-rule-7-1"]
               .map((slug) => getTermBySlug(slug))
               .filter(Boolean)
               .map((t) => (
                 <a
                   key={t!.slug}
                   href={`/glossary/${t!.slug}`}
                   className="rounded-full border border-border bg-card px-4 py-1.5 text-sm font-medium text-foreground transition hover:border-[var(--gold)] hover:text-[var(--gold-deep)]"
                 >
                   {t!.term}
                 </a>
               ))}
           </div>
           <a
             href="/glossary"
             className="mt-7 inline-flex items-center justify-center gap-2 rounded-md border border-primary/30 px-6 py-3 text-center text-sm font-semibold text-primary transition hover:bg-primary/5"
           >
             Browse all {glossaryTerms.length} terms
             <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
           </a>
         </div>
       </Section>

       {/* FAQ */}
      <Section>
        <div className="grid md:grid-cols-3 gap-12">
          <div className="md:col-span-1">
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl font-semibold text-foreground">Answers before you book the call.</h2>
            <p className="mt-5 text-muted-foreground">Still wondering something? Drop us a note on the contact page, a real strategist replies, usually same-day.</p>
            <Link to="/contact" className="mt-6 inline-block text-sm font-semibold text-primary hover:text-[var(--gold-deep)]">Ask a question →</Link>
          </div>
          <div className="md:col-span-2">
            <Accordion type="single" collapsible className="w-full">
              {homeFaqs.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`}>
                  <AccordionTrigger className="text-left text-base md:text-lg font-semibold text-foreground py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-foreground/80 leading-relaxed text-base pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </Section>

      <TalkToStrategist />
    </Layout>
  );
}
